import React from 'react';
import { Typography, Link } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { CommunityInviteCard } from '../components/community/CommunityInviteCard';
import {
  extractCommunityInviteFromText,
  stripCommunityInviteUrls,
} from './communityInviteUrl';
import { linkifyText } from './linkifyText';

interface MentionRendererProps {
  content: string | undefined | null;
  onMentionClick?: (username: string) => void;
}

export const MentionRenderer: React.FC<MentionRendererProps> = ({
  content,
  onMentionClick,
}) => {
  if (!content) {
    return null;
  }

  const communityInvite = extractCommunityInviteFromText(content);
  const workingContent = communityInvite ? stripCommunityInviteUrls(content) : content;

  if (!workingContent && !communityInvite) {
    return null;
  }

  const parseContent = (text: string) => {
    const mentionRegex = /@(\w+)/g;
    const parts: Array<{ type: 'text' | 'mention'; content: string; username?: string }> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = mentionRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index),
        });
      }

      parts.push({
        type: 'mention',
        content: match[0],
        username: match[1],
      });

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex),
      });
    }

    return parts.length ? parts : [{ type: 'text' as const, content: text }];
  };

  const parts = workingContent ? parseContent(workingContent) : [];

  const handleMentionClick = (username: string) => {
    onMentionClick?.(username);
  };

  return (
    <>
      {workingContent && (
        <Typography
          variant="body1"
          sx={{
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            fontSize: 14,
            lineHeight: 1.4,
          }}
        >
          {parts.map((part, index) => {
            if (part.type === 'mention') {
              return (
                <Link
                  key={index}
                  component="span"
                  onClick={() => handleMentionClick(part.username!)}
                  sx={{
                    color: '#1976d2',
                    backgroundColor: '#e3f2fd',
                    padding: '2px 6px',
                    borderRadius: '12px',
                    textDecoration: 'none',
                    fontWeight: 500,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: 'inherit',
                    '&:hover': {
                      backgroundColor: '#bbdefb',
                      textDecoration: 'none',
                    },
                    '&:active': {
                      backgroundColor: '#90caf9',
                    },
                  }}
                >
                  <PersonIcon sx={{ fontSize: 14 }} />
                  {part.content}
                </Link>
              );
            }

            return <span key={index}>{linkifyText(part.content)}</span>;
          })}
        </Typography>
      )}

      {communityInvite && (
        <CommunityInviteCard
          token={communityInvite.token}
          href={communityInvite.href}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </>
  );
};
